import { Constants } from '../Utils/Constants';
import { CrazyMegaEpicScalingStructure, GetScalingInfo, setTextFontScaledToBox } from '../Utils/HelperFunction';
import { Language } from '../GUI/Language';
import Core_Images from 'Core/Assets/Graphics/**/*.png';
import Core_MP3 from 'Core/Assets/Audio/*.mp3';
import Core_M4A from 'Core/Assets/Audio/*.m4a';
import Core_Xml from 'Core/Assets/**/*.xml';
import Core_Json from 'Core/Assets/**/*.json';
import { Client } from '@core/Client';

export abstract class LoadGame extends Phaser.Scene {
	public portraitMode: boolean = false;
	public staticHeight: number = window.innerHeight;
	public staticWidth: number = window.innerWidth;

	protected gameName: string = "";

	protected lastPortraitModeValue: boolean = false;
	protected lastValue: number = 0;

	protected randomMaxLoaderValue: number = 0.95;

	protected loadingGraphics;
	protected loadingBackground: Phaser.GameObjects.Image;
	protected loadingText: Phaser.GameObjects.Text;
	protected gameNameText: Phaser.GameObjects.Text;

	constructor() {
		super({
			key: Constants.SCENES.LOAD,
			pack: {
				files: [{ type: 'image', key: Constants.LOADING_BACKGROUND, url: 'loadingBackground.png' }]
			}
		});
	}

	preload(): void {
		this.randomMaxLoaderValue = 0.95 + Math.random() * 0.04;
		//this.load.setCORS(value) where value is whatever the server expects, you can try anonymous as the value but if the server isn't set-up to support that, it won't work
		this.load.setCORS("*");

		this.load.on('loaderror', () => console.log("A file errored out on loading!"))

		//Loading bar stuff-----------------------------------
		this.loadingBackground = this.add.image(0, 0, Constants.LOADING_BACKGROUND);
		this.loadingBackground.setOrigin(0);

		this.loadingGraphics = this.add.graphics();

		this.loadingText = this.add.text(0, 0, "");
		this.loadingText.setStyle(Constants.LOADING_FONT);
		this.loadingText.setOrigin(0.5, 0.5);

		this.gameNameText = this.add.text(0, 0, this.gameName);
		this.gameNameText.setStyle(Constants.LOADING_FONT);
		this.gameNameText.setOrigin(0.5, 1);

		this.tweens.add({
			targets: [this.gameNameText],
			scaleY: { from: 0.5, to: 1 },
			alpha: { from: 0, to: 1 },
			ease: 'Linear',
			duration: 250,
			repeat: 0
		});

		this.drawProgressBar();
		//----------------------------------------------------

		this.LoadGraphics();
		this.LoadFonts();
		this.LoadSounds();
		this.LoadPlugins();

		this.load.on('progress', function (value: number) {

			//this value represents assets loaded, but a final freeze happens from code creating the main scene. So we will stop it at 98% since we use this value as an overall loading animation.
			if (value > this.randomMaxLoaderValue)
				value = this.randomMaxLoaderValue;

			this.drawProgressBar(value);
		}, this)

		this.load.on('complete', function () {
			this.loadingGraphics.destroy();
			this.loadingText.destroy();
			this.gameNameText.destroy();

			console.log("Total Complete: " + this.load.totalComplete);
			console.log("Total Failed: " + this.load.totalFailed);
		}.bind(this))
	}

	public create(): void {
	}

	protected createMainScene(): void { }

	LoadGraphics(): void {
		this.load.image(Constants.SPRITESHEET_GAME_BACKGROUND, Core_Images.background);

		this.load.atlas(Constants.SPRITESHEET_WINS, Core_Images.WinAtlas, Core_Json.Graphics.WinAtlas);
		this.load.atlas(Constants.SPRITESHEET_MENU, Core_Images.GameMenu, Core_Json.Graphics.GameMenu);

		this.load.atlas(Constants.ANIMATIONS.HELPHAND, Core_Images.helpHand, Core_Json.Graphics.helpHand);

		// if electron, we think we are on a slot machine , so load exit button and banner
		if (Client.slot) {
			this.load.atlas(Constants.SLOTATLAS, Core_Images.slotAtlas, Core_Json.Graphics.slotAtlas);
		}

		if (this.game.device.os.iOS)
			this.load.atlas(Constants.SPRITESHEET_IOS_TUTORIAL, Core_Images.iosHelp, Core_Json.Graphics.iosHelp);
	}

	LoadFonts(): void {
		this.load.bitmapFont('montserrat', Core_Images.Fonts.montserrat, Core_Xml.Graphics.Fonts.montserrat);
	}

	LoadSounds(): void {
		this.load.audio(Constants.SOUNDS.WIN_1, [Core_MP3.win_1, Core_M4A.win_1]);
		this.load.audio(Constants.SOUNDS.WIN_2, [Core_MP3.win_2, Core_M4A.win_2]);
		this.load.audio(Constants.SOUNDS.WIN_3, [Core_MP3.win_3, Core_M4A.win_3]);

		this.load.audio(Constants.SOUNDS.CHIPS_DROPS, [Core_MP3.Chips_Drops, Core_M4A.Chips_Drops]);
		this.load.audio(Constants.SOUNDS.FINANCIALS_CLICK, [Core_MP3.VintageButton2, Core_M4A.VintageButton2]);

		//-----Bet chip sounds---------
		this.load.audio(Constants.SOUNDS.BET_PLACE_SINGLE_1, [Core_MP3.chipSingle, Core_M4A.chipSingle]);
		this.load.audio(Constants.SOUNDS.BET_PLACE_SINGLE_2, [Core_MP3.chipSingle2, Core_M4A.chipSingle2]);
		this.load.audio(Constants.SOUNDS.BET_PLACE_SINGLE_3, [Core_MP3.chipSingle3, Core_M4A.chipSingle3]);
		this.load.audio(Constants.SOUNDS.BET_PLACE_SINGLE_4, [Core_MP3.chipSingle4, Core_M4A.chipSingle4]);
		this.load.audio(Constants.SOUNDS.BET_PLACE_SINGLE_5, [Core_MP3.chipSingle5, Core_M4A.chipSingle5]);

		this.load.audio(Constants.SOUNDS.BET_PLACE_MULTIPLE, [Core_MP3.chipsMultiple, Core_M4A.chipsMultiple]);

		this.load.audio(Constants.SOUNDS.BET_CLEAR_CURRENT, [Core_MP3.bet_clear_current, Core_M4A.bet_clear_current]);

		this.load.audio(Constants.SOUNDS.PAYIN, [Core_MP3.electronic_payin, Core_M4A.electronic_payin]);
		this.load.audio(Constants.SOUNDS.PAYOUT, [Core_MP3.electronic_payout, Core_M4A.electronic_payout]);

		this.load.audio(Constants.SOUNDS.BUTTON_PRESS_SOFT, [Core_MP3.button_press_soft, Core_M4A.button_press_soft]);
		this.load.audio(Constants.SOUNDS.BUTTON_PRESS, [Core_MP3.button_press, Core_M4A.button_press]);
		this.load.audio(Constants.SOUNDS.CHIP_SELECT, [Core_MP3.chip_select, Core_M4A.chip_select]);
	}

	LoadPlugins(): void { }

	drawProgressBar(value?: number): void {
		this.lastValue = value;
		this.lastPortraitModeValue = this.portraitMode;

		this.resize(GetScalingInfo());

		let loadingBarLength = this.staticWidth / 2;
		let loadingBarHeight = loadingBarLength / 10;

		this.loadingBackground.setDisplaySize(this.staticWidth, this.staticHeight);

		this.loadingGraphics.clear();
		this.loadingGraphics.lineStyle(4, 0xffffff);

		setTextFontScaledToBox(this.gameNameText, loadingBarLength / 1.5, loadingBarHeight / 1.5);

		if (this.portraitMode) {
			this.loadingGraphics.strokeRect(this.staticWidth / 2 - loadingBarLength / 2, this.staticHeight / 2 - loadingBarHeight / 2 - this.staticHeight, loadingBarLength, loadingBarHeight);
			this.gameNameText.setPosition(this.staticWidth / 2, this.staticHeight / 2 - this.staticHeight - this.gameNameText.displayHeight);
			this.loadingBackground.setPosition(0, 0 - this.staticHeight);
		}
		else {
			this.loadingGraphics.strokeRect(this.staticWidth / 2 - loadingBarLength / 2, this.staticHeight / 2 - loadingBarHeight / 2, loadingBarLength, loadingBarHeight);
			this.gameNameText.setPosition(this.staticWidth / 2, this.staticHeight / 2 - this.gameNameText.displayHeight);
			this.loadingBackground.setPosition(0, 0);
		}

		if (value) {
			this.loadingText.text = Math.round(value * 100).toString() + " %";
			setTextFontScaledToBox(this.loadingText, loadingBarLength, loadingBarHeight);

			this.loadingGraphics.fillGradientStyle(0x2bba00, 0x2bba00, 0x124d00, 0x124d00, 1);
			if (this.portraitMode) {
				this.loadingGraphics.fillRect((this.staticWidth / 2 - loadingBarLength / 2) + 6, (this.staticHeight / 2 - loadingBarHeight / 2) + 6 - this.staticHeight, (loadingBarLength * value) - 12, loadingBarHeight - 12);
				this.loadingText.setPosition(this.staticWidth / 2, this.staticHeight / 2 - this.staticHeight);
			}
			else {
				this.loadingGraphics.fillRect((this.staticWidth / 2 - loadingBarLength / 2) + 6, (this.staticHeight / 2 - loadingBarHeight / 2) + 6, (loadingBarLength * value) - 12, loadingBarHeight - 12);
				this.loadingText.setPosition(this.staticWidth / 2, this.staticHeight / 2);
			}
		}
	}

	resize(scaling: CrazyMegaEpicScalingStructure): void {
		if (!this.game.device.os.desktop) {
			let body = document.getElementById("body")
			if (!body)
				return

			if (document.activeElement && document.activeElement.tagName !== "BODY") {
				body.style.overflowY = "auto";
				return;
			} else {
				body.style.overflowY = "hidden";
			}
		}

		try {
			switch (window.orientation) {
				case 0:
				case 180://portrait -> switch width and height, we are always landscape view on mobile
					this.staticHeight = scaling.width;
					this.staticWidth = scaling.height;
					this.portraitMode = true;
					break;
				case -90:
				case 90:
				default: //landscape
					this.staticHeight = scaling.height;
					this.staticWidth = scaling.width;
					this.portraitMode = false;
					break;
			}
		}
		catch {
			this.staticHeight = scaling.height;
			this.staticWidth = scaling.width;
			this.portraitMode = false;
		}

		this.scale.zoom = scaling.zoom;
		// manually resize the game with the Phaser 3.16 scalemanager
		this.scale.resize(scaling.width, scaling.height);
		// Scale the camera
		this.cameras.main.setViewport(0, 0, scaling.width, scaling.height);
		this.cameras.resize(scaling.width, scaling.height) // this resizes everything

		if (this.portraitMode) {
			this.cameras.cameras[0].setOrigin(0, 0);
			this.cameras.cameras[0].setRotation(Phaser.Math.DegToRad(90))
		} else {
			this.cameras.cameras[0].setRotation(Phaser.Math.DegToRad(0))
		}

		if (this.portraitMode != this.lastPortraitModeValue)
			this.drawProgressBar(this.lastValue);
	}
}

export default LoadGame;
